import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createIdentityClient, type IdentityPaths } from "@tcg/api-client";
import { staffSelfTopic } from "@tcg/realtime";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/use-auth";
import { isAdminLocale } from "@/i18n";
import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { useLiveTopics } from "@/realtime/hooks";

import {
  hydratePreferences,
  LOCALE_CHOSEN,
  type PreferencesRemote,
  preferencesVersion,
  setPreferencesRemote,
  takeLocaleChoice,
} from "./store";

export type MeOut =
  IdentityPaths["/v1/auth/me"]["get"]["responses"][200]["content"]["application/json"];

export const meQueryKey = (staffId: string) => ["identity", "auth", "me", staffId] as const;

type IdentityClient = ReturnType<typeof createIdentityClient>;

function remoteOf(identity: IdentityClient): PreferencesRemote {
  return {
    patch: async (body) => {
      const out = await unwrap(identity.PATCH("/v1/auth/me", { body }));
      return { preferences: out.preferences ?? {}, preferencesVersion: out.preferencesVersion };
    },
  };
}

/**
 * Keeps the signed-in person's preferences (T01-17) in step with identity-service: loads
 * `GET /v1/auth/me` after sign-in, reloads it when the hub says they changed on another computer
 * (`staff-self:<staffId>`, R21) or the language/record changed, and sends the waiting writes.
 * The UI language follows the person once they picked one themself (T11-03).
 */
export function PreferencesSync({ staffId }: { staffId: string }) {
  const identity = useServiceClient(createIdentityClient);
  const remote = useMemo(() => remoteOf(identity), [identity]);
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const { state } = useAuth();
  const key = meQueryKey(staffId);

  useEffect(() => {
    setPreferencesRemote(staffId, remote);
    return () => {
      setPreferencesRemote(staffId, null);
    };
  }, [staffId, remote]);

  const me = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => unwrap(identity.GET("/v1/auth/me", { signal })),
    enabled: state.status === "authenticated",
    staleTime: 60_000,
    meta: { silent: true },
    retry: false,
  });

  useLiveTopics(
    [staffSelfTopic(staffId) ?? ""].filter(Boolean),
    (message) => {
      // our own save comes back with the version we already hold — nothing to load
      if (
        message.entity === "staff_preferences" &&
        message.version !== null &&
        message.version <= preferencesVersion(staffId)
      ) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: key });
    },
    { onResync: () => void queryClient.invalidateQueries({ queryKey: key }) },
  );

  const applied = useRef<string | null>(null);
  const data = me.data;
  useEffect(() => {
    if (!data) {
      return;
    }
    hydratePreferences(staffId, {
      preferences: data.preferences ?? {},
      preferencesVersion: data.preferencesVersion,
    });
    const picked = takeLocaleChoice();
    if (picked) {
      // chosen on the sign-in page: it becomes the person's language
      applied.current = picked;
      void remote
        .patch({ locale: picked, preferences: { [LOCALE_CHOSEN]: true } })
        .then((answer) => {
          hydratePreferences(staffId, answer);
        })
        .catch(() => undefined);
      return;
    }
    const chosen = data.preferences?.[LOCALE_CHOSEN] === true;
    if (chosen && isAdminLocale(data.locale) && applied.current !== data.locale) {
      applied.current = data.locale;
      if (i18n.resolvedLanguage !== data.locale) {
        void i18n.changeLanguage(data.locale);
      }
    }
  }, [data, staffId, remote, i18n]);

  return null;
}

/** Mounted once in the app: syncs whoever is signed in on this tab. */
export function SignedInPreferencesSync() {
  const { session } = useAuth();
  const staffId = session?.user.id ?? null;
  return staffId ? <PreferencesSync key={staffId} staffId={staffId} /> : null;
}
